import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

import { BLUE, EMPTY, MAX_LEVEL, RED } from "../game/engine.js";
import { DEFAULT_VISUAL_CONFIG, normalizeVisualConfig } from "./visualConfig.js";

const HEX_RADIUS = 1;

function smoothstep(value) {
  return value * value * (3 - 2 * value);
}

function orbLayout(level) {
  if (level <= 1) return [[0, 0]];
  const radius = Math.min(0.36, 0.22 + level * 0.024);
  return Array.from({ length: level }, (_, index) => {
    const angle = (index / level) * Math.PI * 2;
    return [Math.cos(angle) * radius, Math.sin(angle) * radius];
  });
}

function axialToWorld(q, r, size = HEX_RADIUS) {
  return new THREE.Vector3(
    size * Math.sqrt(3) * (q + r * 0.5),
    0,
    size * 1.5 * r,
  );
}

export class HexplodeScene {
  constructor(container, { onCellSelect, canSelect, onExplosion }) {
    this.container = container;
    this.onCellSelect = onCellSelect;
    this.canSelect = canSelect;
    this.onExplosion = onExplosion;
    this.startedAt = performance.now();
    this.pointer = new THREE.Vector2();
    this.raycaster = new THREE.Raycaster();
    this.cellViews = [];
    this.tileMeshes = [];
    this.boardExtent = 0;
    this.hovered = null;
    this.currentPlayer = RED;
    this.locked = false;
    this.quality = "cinematic";
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.pointerDown = null;
    this.pointerMoved = false;
    this.draggedOnLastPointer = false;
    this.visualConfig = { ...DEFAULT_VISUAL_CONFIG };
    this.refreshPalette();
    this.baseBloomStrength = this.visualConfig.bloomStrength;
    this.bloomImpulse = 0;
    this.shake = 0;
    this.celebrating = false;
    this.celebrationId = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x050709);
    this.scene.fog = new THREE.FogExp2(0x050709, 0.012);

    this.camera = new THREE.PerspectiveCamera(42, 1, 0.1, 140);
    this.camera.position.set(0, 12, 14);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = this.visualConfig.exposure;
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.055;
    this.controls.enablePan = false;
    this.controls.minPolarAngle = 0.28;
    this.controls.maxPolarAngle = 1.25;
    this.controls.minDistance = 5;
    this.controls.maxDistance = 28;
    this.controls.target.set(0, 0, 0);

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      this.baseBloomStrength,
      this.visualConfig.bloomRadius,
      this.visualConfig.bloomThreshold,
    );
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(new OutputPass());

    this.boardGroup = new THREE.Group();
    this.boardGroup.name = "hexplode-board";
    this.scene.add(this.boardGroup);
    this.effectsGroup = new THREE.Group();
    this.boardGroup.add(this.effectsGroup);

    this.createEnvironment();
    this.bindInput();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.renderLoop();
  }

  createEnvironment() {
    const ambient = new THREE.HemisphereLight(0xd7edff, 0x271014, 2.1);
    this.scene.add(ambient);

    const key = new THREE.DirectionalLight(0xffffff, 4.4);
    key.position.set(-7, 13, 8);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 45;
    key.shadow.camera.left = -14;
    key.shadow.camera.right = 14;
    key.shadow.camera.top = 14;
    key.shadow.camera.bottom = -14;
    this.scene.add(key);

    const redRim = new THREE.PointLight(0xff1644, 18, 30, 2);
    redRim.position.set(-10, 4, -4);
    this.scene.add(redRim);
    const blueRim = new THREE.PointLight(0x008dff, 20, 30, 2);
    blueRim.position.set(10, 5, 3);
    this.scene.add(blueRim);

    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(24, 96),
      new THREE.MeshStandardMaterial({ color: 0x0a0e10, roughness: 0.9, metalness: 0.25 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.18;
    floor.receiveShadow = true;
    this.scene.add(floor);

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x295363,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.environmentRings = [];
    for (const radius of [8.5, 12.5, 17]) {
      const ring = new THREE.Mesh(new THREE.RingGeometry(radius, radius + 0.025, 128), ringMaterial.clone());
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -0.15;
      this.scene.add(ring);
      this.environmentRings.push(ring);
    }

    const starCount = 900;
    const positions = new Float32Array(starCount * 3);
    for (let index = 0; index < starCount; index += 1) {
      const radius = 18 + Math.random() * 45;
      const theta = Math.random() * Math.PI * 2;
      const y = 3 + Math.random() * 24;
      positions[index * 3] = Math.cos(theta) * radius;
      positions[index * 3 + 1] = y;
      positions[index * 3 + 2] = Math.sin(theta) * radius;
    }
    const starsGeometry = new THREE.BufferGeometry();
    starsGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    this.stars = new THREE.Points(
      starsGeometry,
      new THREE.PointsMaterial({
        color: 0xbceaff,
        size: 0.055,
        transparent: true,
        opacity: 0.7,
        depthWrite: false,
      }),
    );
    this.scene.add(this.stars);
  }

  bindInput() {
    const canvas = this.renderer.domElement;
    canvas.addEventListener("pointerdown", (event) => {
      this.pointerDown = { x: event.clientX, y: event.clientY };
      this.pointerMoved = false;
      this.draggedOnLastPointer = false;
      canvas.dataset.pointerEvent = "down";
    });
    canvas.addEventListener("pointermove", (event) => {
      if (this.pointerDown) {
        const distance = Math.hypot(event.clientX - this.pointerDown.x, event.clientY - this.pointerDown.y);
        if (distance > 7) this.pointerMoved = true;
      }
      this.updateHover(event);
    });
    canvas.addEventListener("pointerleave", () => this.setHovered(null));
    canvas.addEventListener("pointerup", () => {
      this.draggedOnLastPointer = this.pointerMoved;
      this.pointerDown = null;
      canvas.dataset.pointerEvent = this.draggedOnLastPointer ? "dragged" : "up";
    });
    canvas.addEventListener("click", (event) => {
      canvas.dataset.pointerEvent = "click";
      if (this.draggedOnLastPointer || this.locked) {
        canvas.dataset.lastSelection = this.draggedOnLastPointer ? "drag" : "locked";
        return;
      }
      const cell = this.pickCell(event);
      canvas.dataset.lastSelection = cell === null ? "miss" : String(cell);
      if (cell !== null && this.canSelect(cell)) this.onCellSelect(cell);
    });
  }

  eventPointer(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  pickCell(event) {
    this.eventPointer(event);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.intersectObjects(this.tileMeshes, false)[0];
    return hit ? hit.object.userData.cellIndex : null;
  }

  updateHover(event) {
    if (this.locked) return this.setHovered(null);
    const index = this.pickCell(event);
    this.setHovered(index !== null && this.canSelect(index) ? index : null);
  }

  setHovered(index) {
    if (this.hovered === index) return;
    if (this.hovered !== null) this.applyTileMaterial(this.hovered);
    this.hovered = index;
    this.renderer.domElement.dataset.hoveredCell = index === null ? "" : String(index);
    this.renderer.domElement.style.cursor = index === null ? "grab" : "pointer";
    if (index !== null) {
      const view = this.cellViews[index];
      view.material.emissive.copy(this.palette.tileGlow[this.currentPlayer]);
      view.material.emissiveIntensity = this.visualConfig.hoverGlow;
      view.group.scale.setScalar(1.035);
    }
  }

  setCurrentPlayer(player) {
    this.currentPlayer = player;
    if (this.hovered !== null) this.setHovered(null);
  }

  setInteractionLocked(locked) {
    this.locked = locked;
    if (locked) this.setHovered(null);
  }

  setQuality(quality) {
    this.quality = quality;
    const pixelRatio = quality === "cinematic" ? 2 : 1.35;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, pixelRatio));
    this.baseBloomStrength = this.visualConfig.bloomStrength * (quality === "cinematic" ? 1 : 0.67);
    this.renderer.shadowMap.enabled = quality === "cinematic";
    this.resize();
  }

  refreshPalette() {
    const config = this.visualConfig;
    this.palette = {
      tile: {
        [EMPTY]: new THREE.Color(config.neutralTile),
        [RED]: new THREE.Color(config.redTile),
        [BLUE]: new THREE.Color(config.blueTile),
      },
      tileGlow: {
        [EMPTY]: new THREE.Color(0x10272d),
        [RED]: new THREE.Color(config.redTileGlow),
        [BLUE]: new THREE.Color(config.blueTileGlow),
      },
      orb: {
        [RED]: new THREE.Color(config.redOrb),
        [BLUE]: new THREE.Color(config.blueOrb),
      },
      hot: {
        [RED]: new THREE.Color(config.redHot),
        [BLUE]: new THREE.Color(config.blueHot),
      },
    };
  }

  setVisualConfig(config) {
    this.visualConfig = normalizeVisualConfig(config);
    this.refreshPalette();
    this.renderer.toneMappingExposure = this.visualConfig.exposure;
    this.bloomPass.radius = this.visualConfig.bloomRadius;
    this.bloomPass.threshold = this.visualConfig.bloomThreshold;
    this.baseBloomStrength = this.visualConfig.bloomStrength * (this.quality === "cinematic" ? 1 : 0.67);

    const hovered = this.hovered;
    this.hovered = null;
    for (const view of this.cellViews) {
      this.applyTileMaterial(view.tile.userData.cellIndex);
      this.rebuildOrbs(view, view.owner, view.level);
    }
    if (hovered !== null) this.setHovered(hovered);
  }

  buildBoard(board) {
    this.board = board;
    for (const view of this.cellViews) {
      view.material.dispose();
      view.outline.material.dispose();
    }
    while (this.boardGroup.children.length) {
      this.boardGroup.remove(this.boardGroup.children[this.boardGroup.children.length - 1]);
    }
    this.cellViews = [];
    this.tileMeshes = [];
    this.boardExtent = 0;

    const tileGeometry = new THREE.CylinderGeometry(HEX_RADIUS, HEX_RADIUS, 0.2, 6, 1, false);
    const edgeGeometry = new THREE.EdgesGeometry(tileGeometry, 25);
    this.ballGeometry = new THREE.SphereGeometry(0.135, 20, 14);

    for (const cell of board.cells) {
      const group = new THREE.Group();
      const position = axialToWorld(cell.q, cell.r);
      group.position.copy(position);
      this.boardExtent = Math.max(this.boardExtent, Math.hypot(position.x, position.z) + 1.05);

      const material = new THREE.MeshPhysicalMaterial({
        color: this.palette.tile[EMPTY],
        emissive: 0x000000,
        metalness: 0.72,
        roughness: 0.32,
        clearcoat: 0.62,
        clearcoatRoughness: 0.18,
      });
      const tile = new THREE.Mesh(tileGeometry, material);
      tile.position.y = 0.02;
      tile.castShadow = true;
      tile.receiveShadow = true;
      tile.userData.cellIndex = cell.index;
      group.add(tile);

      const outline = new THREE.LineSegments(
        edgeGeometry,
        new THREE.LineBasicMaterial({ color: 0x78909a, transparent: true, opacity: 0.26 }),
      );
      outline.position.y = 0.025;
      group.add(outline);

      const orbGroup = new THREE.Group();
      orbGroup.position.y = 0.28;
      orbGroup.rotation.y = Math.random() * Math.PI * 2;
      group.add(orbGroup);

      this.boardGroup.add(group);
      this.tileMeshes.push(tile);
      this.cellViews[cell.index] = {
        group,
        tile,
        material,
        outline,
        orbGroup,
        owner: EMPTY,
        level: -1,
      };
    }

    this.effectsGroup = new THREE.Group();
    this.boardGroup.add(this.effectsGroup);
    this.frameBoard(false);
  }

  applyTileMaterial(index) {
    const view = this.cellViews[index];
    if (!view) return;
    const empty = view.owner === EMPTY;
    view.material.color
      .copy(this.palette.tile[view.owner])
      .multiplyScalar(empty ? this.visualConfig.neutralBrightness : this.visualConfig.tileBrightness);
    view.material.emissive.copy(this.palette.tileGlow[view.owner]);
    view.material.emissiveIntensity = empty ? 0.16 : this.visualConfig.tileGlow;
    view.outline.material.color.copy(empty ? new THREE.Color(0x6f858d) : this.palette.tile[view.owner]);
    view.outline.material.opacity = empty ? 0.42 : this.visualConfig.tileOutline;
    view.group.scale.setScalar(1);
  }

  sync(owners, levels, { force = false } = {}) {
    for (let index = 0; index < this.cellViews.length; index += 1) {
      const view = this.cellViews[index];
      const owner = owners[index];
      const level = levels[index];
      if (!force && owner === view.owner && level === view.level) continue;
      view.owner = owner;
      view.level = level;
      this.applyTileMaterial(index);
      this.rebuildOrbs(view, owner, level);
    }
    if (this.hovered !== null) this.setHovered(this.hovered);
  }

  rebuildOrbs(view, owner, level) {
    const oldMaterials = new Set(view.orbGroup.children.map((child) => child.material));
    while (view.orbGroup.children.length) view.orbGroup.remove(view.orbGroup.children[0]);
    for (const material of oldMaterials) material?.dispose();
    if (owner === EMPTY || level <= 0) return;
    const charge = THREE.MathUtils.clamp((level - 1) / (MAX_LEVEL - 2), 0, 1);
    const whitening = Math.pow(charge, this.visualConfig.whiteningPower);
    const orbColor = this.palette.orb[owner]
      .clone()
      .lerp(this.palette.hot[owner], THREE.MathUtils.clamp(whitening * this.visualConfig.orbHighlightMix, 0, 1));
    const orbGlow = this.palette.orb[owner]
      .clone()
      .lerp(
        this.palette.hot[owner],
        THREE.MathUtils.clamp(
          this.visualConfig.orbBaseWhitening + whitening * this.visualConfig.orbGlowWhiteMix,
          0,
          1,
        ),
      );
    const material = new THREE.MeshPhysicalMaterial({
      color: orbColor,
      emissive: orbGlow,
      emissiveIntensity: this.visualConfig.orbBaseGlow
        + level * this.visualConfig.orbGlowPerBall
        + whitening * this.visualConfig.primedGlowBoost,
      metalness: 0.15,
      roughness: 0.13,
      clearcoat: 1,
      clearcoatRoughness: 0.08,
    });
    const layout = orbLayout(Math.min(MAX_LEVEL, level));
    for (const [x, z] of layout) {
      const orb = new THREE.Mesh(this.ballGeometry, material);
      orb.position.set(x, 0, z);
      orb.castShadow = true;
      view.orbGroup.add(orb);
    }
  }

  async animateMove(result, speed = "normal") {
    this.setInteractionLocked(true);
    const multiplier = speed === "fast" || this.reduceMotion ? 0.45 : 1;
    this.sync(result.placed.owners, result.placed.levels);
    await this.bounceCell(result.move, 210 * multiplier);

    for (let index = 0; index < result.events.length; index += 1) {
      const event = result.events[index];
      this.onExplosion?.(event.owner, index);
      await this.animateExplosion(event, 560 * multiplier);
      this.sync(event.owners, event.levels);
    }
    this.sync(result.after.owners, result.after.levels);
    this.setInteractionLocked(false);
  }

  bounceCell(index, duration, { height = 0.22, scaleAmount = 0.06 } = {}) {
    const view = this.cellViews[index];
    if (!view || duration <= 1) return Promise.resolve();
    const startY = view.group.position.y;
    return this.animate(duration, (progress) => {
      const lift = Math.sin(progress * Math.PI);
      view.group.position.y = startY + lift * height;
      const scale = 1 + lift * scaleAmount;
      view.group.scale.setScalar(scale);
    }).finally(() => {
      view.group.position.y = startY;
      view.group.scale.setScalar(1);
    });
  }

  animateExplosion(event, duration) {
    const source = this.cellViews[event.source].group.position.clone();
    const color = this.palette.orb[event.owner];
    const projectileMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const projectileGeometry = new THREE.SphereGeometry(0.11, 14, 10);
    const projectiles = event.affected.map((targetIndex) => {
      const projectile = new THREE.Mesh(projectileGeometry, projectileMaterial);
      projectile.position.copy(source).setY(0.44);
      this.effectsGroup.add(projectile);
      return { mesh: projectile, target: this.cellViews[targetIndex].group.position.clone().setY(0.36) };
    });

    const shockMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.95,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const shockwave = new THREE.Mesh(new THREE.RingGeometry(0.2, 0.29, 64), shockMaterial);
    shockwave.rotation.x = -Math.PI / 2;
    shockwave.position.copy(source).setY(0.18);
    this.effectsGroup.add(shockwave);

    const light = new THREE.PointLight(color, 38, 8, 2);
    light.position.copy(source).setY(1);
    this.effectsGroup.add(light);

    const particleCount = this.quality === "cinematic" ? 34 : 18;
    const particlePositions = new Float32Array(particleCount * 3);
    const velocities = [];
    for (let index = 0; index < particleCount; index += 1) {
      particlePositions[index * 3] = source.x;
      particlePositions[index * 3 + 1] = 0.35;
      particlePositions[index * 3 + 2] = source.z;
      const angle = Math.random() * Math.PI * 2;
      const power = 0.8 + Math.random() * 2.2;
      velocities.push(new THREE.Vector3(Math.cos(angle) * power, 1.5 + Math.random() * 2.2, Math.sin(angle) * power));
    }
    const particleGeometry = new THREE.BufferGeometry();
    particleGeometry.setAttribute("position", new THREE.BufferAttribute(particlePositions, 3));
    const particles = new THREE.Points(
      particleGeometry,
      new THREE.PointsMaterial({
        color,
        size: 0.14,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    this.effectsGroup.add(particles);
    this.shake = Math.max(this.shake, 0.16 + Math.min(0.18, event.affected.length * 0.025));
    this.bloomImpulse = 0.9;

    return this.animate(duration, (rawProgress) => {
      const progress = smoothstep(rawProgress);
      shockwave.scale.setScalar(1 + progress * 8);
      shockMaterial.opacity = 0.95 * (1 - progress);
      light.intensity = 38 * (1 - progress);
      projectileMaterial.opacity = 1 - Math.max(0, (progress - 0.8) * 5);

      for (const { mesh, target } of projectiles) {
        mesh.position.lerpVectors(source, target, progress);
        mesh.position.y += Math.sin(progress * Math.PI) * 1.55;
        mesh.scale.setScalar(1 + Math.sin(progress * Math.PI) * 0.45);
      }

      const positions = particleGeometry.attributes.position.array;
      for (let index = 0; index < particleCount; index += 1) {
        const velocity = velocities[index];
        positions[index * 3] = source.x + velocity.x * progress;
        positions[index * 3 + 1] = 0.35 + velocity.y * progress - 2.4 * progress * progress;
        positions[index * 3 + 2] = source.z + velocity.z * progress;
      }
      particleGeometry.attributes.position.needsUpdate = true;
      particles.material.opacity = 1 - progress;
    }).finally(() => {
      for (const { mesh } of projectiles) this.effectsGroup.remove(mesh);
      this.effectsGroup.remove(shockwave, light, particles);
      projectileGeometry.dispose();
      projectileMaterial.dispose();
      shockwave.geometry.dispose();
      shockMaterial.dispose();
      particleGeometry.dispose();
      particles.material.dispose();
    });
  }

  async celebrate(owner) {
    const celebrationId = ++this.celebrationId;
    this.celebrating = true;
    this.setInteractionLocked(true);
    this.bloomImpulse = 2;
    this.shake = 0.34;
    if (owner === EMPTY) {
      this.celebrating = false;
      this.setInteractionLocked(false);
      return;
    }

    const winningCells = this.cellViews
      .filter((view) => view.owner === owner)
      .sort((a, b) => {
        const aCell = this.board.cells[a.tile.userData.cellIndex];
        const bCell = this.board.cells[b.tile.userData.cellIndex];
        return aCell.distance - bCell.distance || aCell.index - bCell.index;
      });
    const waveDuration = this.reduceMotion ? 160 : 1100;
    const jumpDuration = this.reduceMotion ? 320 : 1080;
    const height = this.reduceMotion ? 0.18 : 0.82;
    const scaleAmount = this.reduceMotion ? 0.03 : 0.14;

    await Promise.all(winningCells.map(async (view, index) => {
      const waveProgress = winningCells.length <= 1 ? 0 : index / (winningCells.length - 1);
      await new Promise((resolve) => window.setTimeout(resolve, waveProgress * waveDuration));
      if (celebrationId !== this.celebrationId) return;
      await this.bounceCell(view.tile.userData.cellIndex, jumpDuration, { height, scaleAmount });
    }));

    if (celebrationId === this.celebrationId) {
      this.celebrating = false;
      this.setInteractionLocked(false);
    }
  }

  stopCelebration() {
    this.celebrationId += 1;
    this.celebrating = false;
    this.setInteractionLocked(false);
  }

  frameBoard(animate = true) {
    if (!this.board) return;
    const aspect = this.container.clientWidth / Math.max(1, this.container.clientHeight);
    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * aspect);
    const limitingFov = Math.min(verticalFov, horizontalFov);
    const portrait = aspect < 1;
    const distance = (this.boardExtent / Math.sin(limitingFov / 2)) * (portrait ? 1.12 : 1.08);
    const targetPosition = new THREE.Vector3(0, 0.63, 0.78).normalize().multiplyScalar(distance);
    this.controls.maxDistance = Math.max(32, distance * 1.6);
    this.framedAspect = aspect;
    if (!animate || this.reduceMotion) {
      this.camera.position.copy(targetPosition);
      this.controls.target.set(0, 0, 0);
      this.controls.update();
      return;
    }
    const start = this.camera.position.clone();
    this.animate(520, (progress) => {
      this.camera.position.lerpVectors(start, targetPosition, smoothstep(progress));
      this.controls.target.lerp(new THREE.Vector3(0, 0, 0), 0.16);
    });
  }

  resize() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    const aspect = width / height;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);
    if (this.board && (!this.framedAspect || Math.abs(Math.log(aspect / this.framedAspect)) > 0.18)) {
      this.frameBoard(false);
    }
  }

  animate(duration, update) {
    if (duration <= 1) {
      update(1);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const started = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        update(progress);
        if (progress < 1) requestAnimationFrame(tick);
        else resolve();
      };
      requestAnimationFrame(tick);
    });
  }

  renderLoop() {
    const elapsed = (performance.now() - this.startedAt) / 1000;
    this.controls.update();
    this.stars.rotation.y = elapsed * 0.012;
    this.environmentRings.forEach((ring, index) => {
      ring.rotation.z = elapsed * (index % 2 ? -0.025 : 0.018);
      ring.material.opacity = 0.12 + Math.sin(elapsed * 0.5 + index) * 0.035;
    });

    for (const view of this.cellViews) {
      if (view.level === MAX_LEVEL - 1) {
        const pulse = 1 + Math.sin(elapsed * 5.2 + view.tile.userData.cellIndex * 0.4) * 0.085;
        view.orbGroup.scale.setScalar(pulse);
      } else {
        view.orbGroup.scale.setScalar(1);
      }
    }

    if (this.celebrating) this.boardGroup.rotation.y += 0.0018;
    this.bloomImpulse *= 0.9;
    this.bloomPass.strength = this.baseBloomStrength + this.bloomImpulse;
    if (this.shake > 0.001) {
      this.camera.position.x += (Math.random() - 0.5) * this.shake;
      this.camera.position.y += (Math.random() - 0.5) * this.shake * 0.5;
      this.shake *= 0.82;
    }
    this.composer.render();
    requestAnimationFrame(() => this.renderLoop());
  }
}
